import { Button } from "@exopengithub/exokit/v2";
import {
	arrow,
	autoUpdate,
	FloatingFocusManager,
	flip,
	offset,
	type Placement,
	shift,
	useFloating,
} from "@floating-ui/react";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useEscapeKey } from "src/common/utils/hooks/useEscapeKey.js";
import type { ExperienceStep } from "./types.js";
import { type AnchorRect, useAnchorRect } from "./useAnchorRect.js";

const GAP = 32;
const VIEWPORT_PADDING = 16;
const ARROW_SIZE = 14;

const third = (position: number, length: number) =>
	position < length / 3 ? 0 : position < (length * 2) / 3 ? 1 : 2;

const placementFor = (rect: AnchorRect): Placement => {
	const column = third(rect.left + rect.width / 2, window.innerWidth);
	const row = third(rect.top + rect.height / 2, window.innerHeight);
	if (column === 1) {
		return row === 2 ? "top" : "bottom";
	}
	const side = column === 0 ? "right" : "left";
	return row === 0 ? `${side}-start` : row === 2 ? `${side}-end` : side;
};

const OPPOSITE_SIDE = {
	bottom: "top",
	left: "right",
	right: "left",
	top: "bottom",
} as const;

export const ExperienceSpotlight = ({
	step,
	stepNumber,
	stepCount,
	onNext,
	onBack,
	onSkip,
}: {
	step: ExperienceStep;
	stepNumber: number;
	stepCount: number;
	onNext: () => void;
	onBack: (() => void) | undefined;
	onSkip: () => void;
}) => {
	const { t } = useTranslation();
	const titleId = useId();
	const bodyId = useId();
	const rect = useAnchorRect(step.anchor);
	const nextRef = useRef<HTMLButtonElement>(null);
	const arrowRef = useRef<HTMLDivElement>(null);

	const { refs, floatingStyles, context, middlewareData, placement, update } =
		useFloating({
			middleware: [
				offset(GAP),
				flip({ padding: VIEWPORT_PADDING }),
				shift({ padding: VIEWPORT_PADDING }),
				arrow({ element: arrowRef, padding: ARROW_SIZE }),
			],
			open: true,
			placement: rect ? placementFor(rect) : "right-start",
			whileElementsMounted: autoUpdate,
		});

	useLayoutEffect(() => {
		if (!rect) {
			return;
		}
		refs.setPositionReference({
			getBoundingClientRect: () =>
				new DOMRect(rect.left, rect.top, rect.width, rect.height),
		});
		update();
	}, [rect, refs, update]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: stepNumber only triggers the refocus; its value isn't read.
	useEffect(() => {
		nextRef.current?.focus();
	}, [stepNumber]);

	useEscapeKey(onSkip);

	if (!rect) {
		return null;
	}

	const side = placement.split("-")[0] as keyof typeof OPPOSITE_SIDE;
	const arrowData = middlewareData.arrow;

	return createPortal(
		<div className="fixed inset-0 z-[1000]">
			<div
				className="pointer-events-none absolute rounded-[2px] shadow-[0_0_0_4px_#fff,0_0_0_9999px_rgb(16_24_40/0.6)] transition-all duration-300 motion-reduce:transition-none"
				style={{
					height: rect.height,
					left: rect.left,
					top: rect.top,
					width: rect.width,
				}}
			/>
			<FloatingFocusManager context={context} initialFocus={nextRef}>
				<div
					aria-describedby={bodyId}
					aria-labelledby={titleId}
					aria-modal
					className="w-[392px] max-w-[calc(100vw-32px)]"
					ref={refs.setFloating}
					role="dialog"
					style={floatingStyles}
				>
					<div
						aria-hidden
						className="absolute size-3.5 rotate-45 rounded-[2px] bg-white"
						ref={arrowRef}
						style={{
							left: arrowData?.x,
							top: arrowData?.y,
							[OPPOSITE_SIDE[side]]: -ARROW_SIZE / 2,
						}}
					/>
					<div
						className="relative flex animate-v2-fade-in flex-col gap-3 rounded-[2px] bg-white p-8 shadow-v2-xl motion-reduce:animate-none"
						key={stepNumber}
					>
						<p className="font-medium text-v2-gray-500 text-v2-xs uppercase tracking-[0.04em]">
							{t("Step {{current}} of {{total}}", {
								current: stepNumber,
								total: stepCount,
							})}
						</p>
						<h2 className="font-semibold text-gray-900 text-v2-lg" id={titleId}>
							{t(step.title)}
						</h2>
						<p className="text-v2-gray-dark text-v2-sm" id={bodyId}>
							{t(step.body)}
						</p>
						<div className="flex items-center justify-end gap-2 pt-3">
							{onBack && (
								<Button
									className="mr-auto"
									onClick={onBack}
									size="sm"
									variant="ghost"
								>
									{t("Back")}
								</Button>
							)}
							{stepNumber < stepCount && (
								<Button onClick={onSkip} size="sm" variant="secondaryGray">
									{t("Skip")}
								</Button>
							)}
							<Button onClick={onNext} ref={nextRef} size="sm">
								{t(step.next)}
							</Button>
						</div>
					</div>
				</div>
			</FloatingFocusManager>
		</div>,
		document.body,
	);
};
